import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: ReactNode;
}

const sizeStyles = {
  sm: 'pl-3 pr-10 py-1.5 text-sm',
  md: 'pl-4 pr-10 py-2.5 text-base',
  lg: 'pl-4 pr-10 py-3 text-lg',
};

const iconSizeStyles = {
  sm: 'pl-9',
  md: 'pl-11',
  lg: 'pl-12',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      hint,
      options,
      placeholder,
      size = 'md',
      leftIcon,
      disabled,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-surface-400">
              {leftIcon}
            </div>
          )}

          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            className={`
              w-full rounded-lg appearance-none
              bg-white dark:bg-surface-800
              border transition-all duration-150
              text-surface-900 dark:text-surface-100
              focus:outline-none focus:ring-2 focus:ring-offset-0
              disabled:bg-surface-100 dark:disabled:bg-surface-900
              disabled:text-surface-500 disabled:cursor-not-allowed
              cursor-pointer
              ${error
                ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20'
                : 'border-surface-300 dark:border-surface-600 focus:border-accent-500 focus:ring-accent-500/20'
              }
              ${sizeStyles[size]}
              ${leftIcon ? iconSizeStyles[size] : ''}
              ${className}
            `}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-surface-400">
            <ChevronDownIcon className="w-5 h-5" />
          </div>
        </div>

        {(error || hint) && (
          <p
            className={`mt-1.5 text-sm ${
              error ? 'text-danger-600 dark:text-danger-400' : 'text-surface-500'
            }`}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
